import { v4 as uuid } from 'uuid';
import { IHistoryItem, type IFramework, type IHistoryCustomData } from '@/framework';
import { Framework } from '@/framework/instance';
import { checkIsHorizontalMultiLine, TextMeasurer } from '@/utils/text-kit';
import { LayerType, MaterialIdsType, TextWritingType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { pushHistory, updateLayer } from '../utils';
import { type IShapeText } from './shape-text';

export type SpecificTextStyle = Pick<ITextLayerProps, 'fontFamily' | 'fontSize' | 'bold' | 'italic'>;

export interface IReplacer {
    /**
     * 批量替换文本图层
     * @param prevLayers - 旧图层数组
     * @param nextLayers - 新图层数组
     * @param historyFlag - 历史记录标志
     */
    replaceLayers(prevLayers: ILayerProps[], nextLayers: ILayerProps[], historyFlag?: string): void;

    /**
     * 根据新的文本内容及原始图层数据，创建新的文本图层
     * @param layer - 原始文本图层
     * @param text - 新的文本内容
     * @param options - 配置选项
     *  `options.resetLayerID` - 是否重置图层 ID，默认为 false
     *  `options.update` - 是否需要更新舞台，默认为 false
     *  `options.triggerFuncID` - 触发该功能的功能 ID，默认为空
     * @returns - 新的文本图层
     */
    replaceLayerText(
        layer: ILayerProps,
        text: string,
        options?: { resetLayerID?: boolean; update?: boolean; triggerFuncID?: string },
    ): Promise<ILayerProps | void>;

    /**
     * 根据指定的样式信息更新文本图层
     * @param layerID - 图层 ID
     * @param style - 部分图层数据
     * @param fontMaterialID - 字体素材 ID
     * @returns - 更新后的文本图层
     */
    updateBySpecificStyle(
        layerID: string,
        style: SpecificTextStyle,
        fontMaterialID?: number,
    ): Promise<ILayerProps | void>;
}

const HISTORY_DATA_KEY = 'REPLACE_TEXT_LAYERS';

type HistoryPayload = {
    prevLayers: ILayerProps[];
    nextLayers: ILayerProps[];
};

export class ReplaceTextLayerHistory extends Framework {
    constructor(identity: string) {
        super(identity);

        this.framework.history.register(HISTORY_DATA_KEY, {
            onRedoUndo: (historyItem: IHistoryItem<HistoryPayload>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const data = historyItem.data.data;
                if (isUndo) {
                    const { prevLayers } = data;
                    prevLayers.forEach((layer) => {
                        this.framework.stage.addOrUpdate(layer);
                    });
                }
                if (isRedo) {
                    const { nextLayers } = data;
                    nextLayers.forEach((layer) => {
                        this.framework.stage.addOrUpdate(layer);
                    });
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<HistoryPayload>) => {
                return {
                    message: [historyItem.data.data.nextLayers],
                };
            },
            onAfterRedoUndo(historyItem: IHistoryItem<HistoryPayload>, isRedo) {
                return {
                    message: [isRedo ? historyItem.data.data.nextLayers : historyItem.data.data.prevLayers],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<HistoryPayload>, isRedo, isUndo, store) => {
                if (isUndo) {
                    const { nextLayers } = historyItem.data.data;
                    for (const originLayer of nextLayers) {
                        historyItem.isDirty = this.framework.checkLayerIsDirty({
                            originLayer,
                            latestIsExtraGroup: this.framework.checkIsExtraGroup(originLayer),
                            latestIsFormalGroup: this.framework.checkIsFormalGroup(originLayer),
                        });
                        if (historyItem.isDirty) break;
                    }
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        const { prevLayers } = historyItem.data.data;
                        for (const originLayer of prevLayers) {
                            historyItem.isDirty = this.framework.checkLayerIsDirty({
                                originLayer,
                                latestIsExtraGroup: this.framework.checkIsExtraGroup(originLayer),
                                latestIsFormalGroup: this.framework.checkIsFormalGroup(originLayer),
                            });
                            if (historyItem.isDirty) break;
                        }
                    }
                }
            },
            onMessage: (message: ILayerProps[]) => {
                this.framework.operate.delActiveLayer();
                message.forEach((item) => {
                    this.framework.stage.addOrUpdate(item);
                });
            },
        });
    }
}

export class Replacer implements IReplacer {
    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    replaceLayers(prevLayers: ILayerProps[], nextLayers: ILayerProps[], historyFlag?: string) {
        this.framework.history.write({
            type: HISTORY_DATA_KEY,
            data: {
                prevLayers,
                nextLayers,
                historyFlag,
            },
        });
    }

    async replaceLayerText(
        layer: ILayerProps,
        text: string,
        options = { resetLayerID: false, update: false, triggerFuncID: undefined, keepConstantWidth: false },
    ) {
        if (layer.layerType !== LayerType.TEXT_LAYER) return;

        const { resetLayerID, update, triggerFuncID, keepConstantWidth } = options;

        const prevLayerData = layer.data as ITextLayerProps;
        const nextLayerData: ITextLayerProps = {
            ...prevLayerData,
            text,
            textContents: undefined,
        };

        let nextLayer: ILayerProps = {
            ...layer,
            data: nextLayerData,
        };

        if (resetLayerID) nextLayer = { ...nextLayer, id: uuid() };

        const { fontFamily } = nextLayerData;
        await this.framework.registerFont(nextLayer, fontFamily);

        const { shapeExtension } = prevLayerData;
        if (!shapeExtension) {
            const textSize = TextMeasurer.measure(nextLayer, {
                keepConstantWidth,
                keepConstantHeight: false,
            });

            nextLayer.width = textSize.width;
            nextLayer.height = textSize.height;
        } else {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;
        }

        if (update) {
            this.framework.stage.addOrUpdate(nextLayer);
            this.framework.operate.setActiveLayer(nextLayer.id);
            this.framework.history.write(layer, nextLayer, triggerFuncID);
        }

        return nextLayer;
    }

    async updateBySpecificStyle(layerID: string, style: SpecificTextStyle, fontMaterialID?: number) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.TEXT_LAYER) return;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const prevTextContents = prevLayerData.textContents || [];
        const nextTextContents = prevTextContents.map((item) => ({ ...item, ...style })); // 分段内容也同时更新

        const nextLayerData: ITextLayerProps = {
            ...prevLayerData,
            ...style,
            textContents: nextTextContents,
        };
        let nextLayer: ILayerProps = {
            ...prevLayer,
            data: nextLayerData,
        };

        const { fontFamily } = style;
        await this.framework.registerFont(nextLayer, fontFamily, true);

        const { shapeExtension } = prevLayerData;
        if (!shapeExtension) {
            const { writingMode, fontSize, lineSpacing } = nextLayerData;
            const textSize = TextMeasurer.measure(nextLayer, {
                keepConstantWidth:
                    writingMode === TextWritingType.HORIZONTAL_TB &&
                    (nextLayer.__fixedDirectionSize ||
                        checkIsHorizontalMultiLine(nextLayer.height, fontSize, lineSpacing)),
                keepConstantHeight: false,
            });

            nextLayer.width = textSize.width;
            nextLayer.height = textSize.height;
        } else {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;
        }

        if (fontMaterialID && nextLayer.materialIds) {
            nextLayer.materialIds[MaterialIdsType.FONT_FAMILY] = [String(fontMaterialID)];
        }

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer);

        return nextLayer;
    }
}
